import { useState, useEffect, useMemo, useLayoutEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Banner,
  Button,
  TextField,
  InlineStack,
  Badge,
  BlockStack,
  Divider,
  Select,
  Grid,
  Box,
  Spinner,
} from '@shopify/polaris';
import { useApiKeyStatus, useValidateApiKey, useRemoveApiKey } from '@/api/api-key';
import { useBranding, useSaveBranding } from '@/api/branding';
import { useWidgetColors, useSaveWidgetColors } from '@/api/widget';
import { getShopDomain } from '@/utils/getShop';
import type { WidgetColors } from '@shared/types/dashboard';
import WidgetPreview from '@/components/WidgetPreview';

const Dashboard = () => {
  const shop = getShopDomain();
  const { data: apiKeyStatus, isLoading: isLoadingApiKey } = useApiKeyStatus(shop);
  const { mutate: validateApiKey, isPending: isValidating } = useValidateApiKey();
  const { mutate: removeApiKey, isPending: isRemoving } = useRemoveApiKey();
  
  const { data: brandingData, isLoading: isLoadingBranding } = useBranding(shop);
  const { mutate: saveBranding, isPending: isSavingBranding } = useSaveBranding();
  
  const { data: widgetColorsData, isLoading: isLoadingColors } = useWidgetColors(shop);
  const { mutate: saveWidgetColors, isPending: isSavingColors } = useSaveWidgetColors();

  const [apiKey, setApiKey] = useState('');
  const [brandColor, setBrandColor] = useState<string | null>(null);
  const [programName, setProgramName] = useState<string | null>(null);
  const [themeMode, setThemeMode] = useState<'auto' | 'light' | 'dark' | null>(null);
  const [widgetColors, setWidgetColors] = useState<WidgetColors | null>(null);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorBanner, setShowErrorBanner] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Initialize form data from API responses
  useEffect(() => {
    if (apiKeyStatus?.success && apiKeyStatus.store) {
      if (apiKeyStatus.store.apiKeyPreview) {
        setApiKey(apiKeyStatus.store.apiKeyPreview.replace('***', ''));
      }
    }
  }, [apiKeyStatus]);

  useLayoutEffect(() => {
    if (brandingData?.success && brandingData.branding) {
      setBrandColor(brandingData.branding.brandColor);
      setProgramName(brandingData.branding.programName);
      setThemeMode(brandingData.branding.themeMode);
    }
  }, [brandingData]);

  useLayoutEffect(() => {
    if (widgetColorsData?.success && widgetColorsData.colors) {
      setWidgetColors(widgetColorsData.colors);
    }
  }, [widgetColorsData]);

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setShowSuccessBanner(true);
    setShowErrorBanner(false);
    setTimeout(() => setShowSuccessBanner(false), 5000);
  };

  const showError = (message: string) => {
    setErrorMessage(message);
    setShowErrorBanner(true);
    setShowSuccessBanner(false);
  };

  const handleValidateApiKey = () => {
    if (!apiKey || apiKey.length !== 32) {
      showError('API key must be exactly 32 characters');
      return;
    }

    validateApiKey(
      { shop, apiKey },
      {
        onSuccess: (data) => {
          if (data.success) {
            showSuccess(data.message || 'API key validated successfully!');
            // Refresh status
            window.location.reload();
          } else {
            showError(data.message || 'Failed to validate API key');
          }
        },
        onError: (error) => {
          showError(error instanceof Error ? error.message : 'Failed to validate API key');
        },
      }
    );
  };

  const handleRemoveApiKey = () => {
    if (!confirm('Are you sure you want to remove the API key? This will disable the loyalty program for your store.')) {
      return;
    }

    removeApiKey(
      { shop },
      {
        onSuccess: (data) => {
          if (data.success) {
            showSuccess(data.message || 'API key removed successfully');
            setApiKey('');
            window.location.reload();
          } else {
            showError(data.message || 'Failed to remove API key');
          }
        },
        onError: (error) => {
          showError(error instanceof Error ? error.message : 'Failed to remove API key');
        },
      }
    );
  };

  const handleSaveBranding = () => {
    if (!brandColor || !programName || !themeMode) {
      showError('Please ensure all branding fields are filled');
      return;
    }
    
    saveBranding(
      {
        shop,
        brandColor,
        programName,
        themeMode,
        ctaButtonColor: null,
      },
      {
        onSuccess: (data) => {
          if (data.success) {
            showSuccess(data.message || 'Branding settings saved successfully!');
          } else {
            showError(data.message || 'Failed to save branding settings');
          }
        },
        onError: (error) => {
          showError(error instanceof Error ? error.message : 'Failed to save branding settings');
        },
      }
    );
  };

  const handleSaveWidgetColors = () => {
    if (!widgetColors) return;

    saveWidgetColors(
      {
        shop,
        colors: widgetColors,
      },
      {
        onSuccess: (data) => {
          if (data.success) {
            showSuccess(data.message || 'Widget colors saved successfully!');
          } else {
            showError(data.message || 'Failed to save widget colors');
          }
        },
        onError: (error) => {
          showError(error instanceof Error ? error.message : 'Failed to save widget colors');
        },
      }
    );
  };

  const handleResetWidgetColors = () => {
    if (!confirm('Are you sure you want to reset all widget colors to defaults?')) {
      return;
    }

    const defaultColors: WidgetColors = {
      cardBgColor: '#ffffff',
      cardHeaderBgColor: '#f5f5f5',
      cardHeaderTextColor: '#000000',
      cardBodyTextColor: '#333333',
      widgetHeaderBgColor: '#f5f5f5',
      widgetHeaderTitleColor: '#000000',
      widgetHeaderSubtitleColor: '#666666',
      customerInfoBgColor: '#fafafa',
      customerLabelColor: '#000000',
      customerValueColor: '#333333',
      pointsDisplayBgColor: '#007bff',
      pointsNumberColor: '#ffffff',
      pointsLabelColor: '#ffffff',
      discountsSectionBgColor: '#ffffff',
      discountsSectionTitleColor: '#333333',
      discountItemBgColor: '#f9f9f9',
      discountItemTitleColor: '#000000',
      discountItemSubtitleColor: '#555555',
      discountButtonBgColor: '#ffcc00',
      discountButtonTextColor: '#000000',
    };

    setWidgetColors(defaultColors);
  };

  const updateWidgetColor = (key: keyof WidgetColors, value: string) => {
    if (!widgetColors) return;
    setWidgetColors({ ...widgetColors, [key]: value });
  };

  const updateWidgetText = (key: keyof WidgetColors, value: string | null) => {
    if (!widgetColors) return;
    setWidgetColors({ ...widgetColors, [key]: value || null });
  };

  const hasValidatedApiKey = apiKeyStatus?.success && apiKeyStatus.store?.apiKeyValidated;
  const store = apiKeyStatus?.success ? apiKeyStatus.store : null;

  // Memoize WidgetPreview props to prevent unnecessary re-renders
  // Use local state values for immediate preview updates when user changes colors
  const widgetPreviewBranding = useMemo(() => {
    // Use local state values - they are set when API data loads
    // Return null if required values are not yet loaded
    if (!programName || !brandColor || themeMode === null) {
      return null;
    }
    
    return {
      programName,
      brandColor,
      ctaButtonColor: brandingData?.branding?.ctaButtonColor || '#ffffff',
      themeMode
    };
  }, [
    programName,
    brandColor,
    themeMode,
    brandingData?.branding?.ctaButtonColor
  ]);

  const widgetPreviewColors = useMemo(() => {
    // Return null if colors are not yet loaded
    return widgetColors || null;
  }, [widgetColors]);

  return (
    <Page
      title="Sticky Cards Dashboard"
      subtitle={`Manage your loyalty program settings for ${shop}`}
    >
      <Layout>
        {showSuccessBanner && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setShowSuccessBanner(false)}>
              <p>{successMessage}</p>
            </Banner>
          </Layout.Section>
        )}

        {showErrorBanner && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setShowErrorBanner(false)}>
              <p>{errorMessage}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* API Key Configuration */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text variant="headingMd" as="h2">
                  🔑 API Configuration
                </Text>
                {store && (
                  <Badge
                    tone={
                      store.apiKeyValidated
                        ? 'success'
                        : store.hasApiKey
                        ? 'attention'
                        : 'critical'
                    }
                  >
                    {store.apiKeyValidated ? '● Connected' : store.hasApiKey ? '● Needs Validation' : '● Setup Required'}
                  </Badge>
                )}
              </InlineStack>

              {isLoadingApiKey ? (
                <Box padding="400">
                  <Spinner accessibilityLabel="Loading API key status" size="small" />
                </Box>
              ) : (
                <>
                  {store?.apiKeyValidated && (
                    <Banner tone="success">
                      <p>
                        ✅ API key is validated and ready to use!
                        {store.apiKeyPreview && (
                          <>
                            <br />
                            <small>Current API Key: {store.apiKeyPreview}</small>
                          </>
                        )}
                      </p>
                    </Banner>
                  )}

                  {store?.hasApiKey && !store.apiKeyValidated && (
                    <Banner tone="info">
                      <p>⚠️ API key is saved but not validated. Please validate it to activate the loyalty program.</p>
                    </Banner>
                  )}

                  {!store?.hasApiKey && (
                    <p>Please enter your Sticky Cards API key to start using the loyalty program features.</p>
                  )}

                  <TextField
                    label="Sticky Cards API Key"
                    value={apiKey}
                    onChange={setApiKey}
                    placeholder="Enter your 32-character API key..."
                    maxLength={32}
                    helpText="API key must be exactly 32 characters (letters and numbers only)"
                    autoComplete="off"
                    type="text"
                  />

                  <InlineStack gap="300">
                    <Button
                      variant="primary"
                      onClick={handleValidateApiKey}
                      loading={isValidating}
                      disabled={!apiKey || apiKey.length !== 32}
                    >
                      {store?.hasApiKey ? 'Update & Validate' : 'Validate API Key'}
                    </Button>

                    {store?.hasApiKey && (
                      <Button
                        variant="primary"
                        tone="critical"
                        onClick={handleRemoveApiKey}
                        loading={isRemoving}
                      >
                        Remove API Key
                      </Button>
                    )}
                  </InlineStack>
                </>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Branding Settings - Only show if API key is validated */}
        {hasValidatedApiKey && (
          <>
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Text variant="headingMd" as="h2">
                    🎨 Sticky Cards Integration Settings
                  </Text>

                  {isLoadingBranding ? (
                    <Box padding="400">
                      <Spinner accessibilityLabel="Loading branding" size="small" />
                    </Box>
                  ) : (
                    <>
                      <InlineStack gap="300" align="start">
                        <input
                          type="color"
                          value={brandColor || '#006eff'}
                          onChange={(e) => setBrandColor(e.target.value)}
                          style={{ width: '60px', height: '40px', cursor: 'pointer', marginTop: '20px' }}
                        />
                        <TextField
                          label="Brand Button Color"
                          value={brandColor || ''}
                          onChange={setBrandColor}
                          placeholder="#006eff"
                          helpText="Used for accents, highlights, and backgrounds in your loyalty popups and banners."
                          autoComplete="off"
                        />
                      </InlineStack>

                      <TextField
                        label="Program Name"
                        value={programName || ''}
                        onChange={setProgramName}
                        placeholder="Enter your program name..."
                        helpText="The name of your loyalty program (e.g., Dank Bank, VIP Rewards, Green Club). This will appear automatically in all popups, modals, and banners."
                        autoComplete="off"
                      />

                      <Select
                        label="Theme Mode"
                        options={[
                          { label: 'Auto', value: 'auto' },
                          { label: 'Light', value: 'light' },
                          { label: 'Dark', value: 'dark' },
                        ]}
                        value={themeMode || 'auto'}
                        onChange={(value) => setThemeMode(value as 'auto' | 'light' | 'dark')}
                        helpText="Controls the background style of your popups and banners. Choose Auto to match your store's look automatically."
                      />

                      <Button
                        variant="primary"
                        onClick={handleSaveBranding}
                        loading={isSavingBranding}
                      >
                        Save Settings
                      </Button>
                    </>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>

            {/* Widget Preview - Only show if API key is validated and all data is loaded */}
            {hasValidatedApiKey && widgetPreviewBranding && widgetPreviewColors && (
              <Layout.Section>
                <WidgetPreview
                  shop={shop}
                  branding={widgetPreviewBranding}
                  colors={widgetPreviewColors}
                />
              </Layout.Section>
            )}

            {/* Widget Colors - Only show if API key is validated */}
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Text variant="headingMd" as="h2">
                    🎨 Widget Preview & Color Customization
                  </Text>

                  {isLoadingColors ? (
                    <Box padding="400">
                      <Spinner accessibilityLabel="Loading widget colors" size="small" />
                    </Box>
                  ) : widgetColors ? (
                    <>
                      <Grid columns={{ xs: 5, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <Grid.Cell columnSpan={{ xs: 6, sm: 4, md: 4, lg: 4, xl: 4 }}>
                          <BlockStack gap="300">
                            <Text variant="headingSm" as="h3">
                              Main Card
                            </Text>
                            <InlineStack gap="200" align="start">
                              <input
                                type="color"
                                value={widgetColors.cardBgColor}
                                onChange={(e) => updateWidgetColor('cardBgColor', e.target.value)}
                                style={{ width: '50px', height: '35px', cursor: 'pointer', marginTop: '20px' }}
                              />
                              <TextField
                                label="Background"
                                value={widgetColors.cardBgColor}
                                onChange={(value) => updateWidgetColor('cardBgColor', value)}
                                autoComplete="off"
                              />
                            </InlineStack>
                            <InlineStack gap="200" align="start">
                              <input
                                type="color"
                                value={widgetColors.cardBodyTextColor}
                                onChange={(e) => updateWidgetColor('cardBodyTextColor', e.target.value)}
                                style={{ width: '50px', height: '35px', cursor: 'pointer', marginTop: '20px' }}
                              />
                              <TextField
                                label="Body Text"
                                value={widgetColors.cardBodyTextColor}
                                onChange={(value) => updateWidgetColor('cardBodyTextColor', value)}
                                autoComplete="off"
                              />
                            </InlineStack>
                          </BlockStack>
                        </Grid.Cell>

                        <Grid.Cell columnSpan={{ xs: 6, sm: 4, md: 4, lg: 4, xl: 4 }}>
                          <BlockStack gap="300">
                            <Text variant="headingSm" as="h3">
                              Widget Header
                            </Text>
                            <InlineStack gap="200" align="start">
                              <input
                                type="color"
                                value={widgetColors.widgetHeaderBgColor}
                                onChange={(e) => updateWidgetColor('widgetHeaderBgColor', e.target.value)}
                                style={{ width: '50px', height: '35px', cursor: 'pointer', marginTop: '20px' }}
                              />
                              <TextField
                                label="Background"
                                value={widgetColors.widgetHeaderBgColor}
                                onChange={(value) => updateWidgetColor('widgetHeaderBgColor', value)}
                                autoComplete="off"
                              />
                            </InlineStack>
                            <InlineStack gap="200" align="start">
                              <input
                                type="color"
                                value={widgetColors.widgetHeaderTitleColor}
                                onChange={(e) => updateWidgetColor('widgetHeaderTitleColor', e.target.value)}
                                style={{ width: '50px', height: '35px', cursor: 'pointer', marginTop: '20px' }}
                              />
                              <TextField
                                label="Title Color"
                                value={widgetColors.widgetHeaderTitleColor}
                                onChange={(value) => updateWidgetColor('widgetHeaderTitleColor', value)}
                                autoComplete="off"
                              />
                            </InlineStack>
                            <InlineStack gap="200" align="start">
                              <input
                                type="color"
                                value={widgetColors.widgetHeaderSubtitleColor}
                                onChange={(e) => updateWidgetColor('widgetHeaderSubtitleColor', e.target.value)}
                                style={{ width: '50px', height: '35px', cursor: 'pointer', marginTop: '20px' }}
                              />
                              <TextField
                                label="Subtitle Color"
                                value={widgetColors.widgetHeaderSubtitleColor}
                                onChange={(value) => updateWidgetColor('widgetHeaderSubtitleColor', value)}
                                autoComplete="off"
                              />
                            </InlineStack>
                          </BlockStack>
                        </Grid.Cell>

                        <Grid.Cell columnSpan={{ xs: 6, sm: 4, md: 4, lg: 4, xl: 4 }}>
                          <BlockStack gap="300">
                            <Text variant="headingSm" as="h3">
                              Customer Info
                            </Text>
                            <InlineStack gap="200" align="start">
                              <input
                                type="color"
                                value={widgetColors.customerInfoBgColor}
                                onChange={(e) => updateWidgetColor('customerInfoBgColor', e.target.value)}
                                style={{ width: '50px', height: '35px', cursor: 'pointer', marginTop: '20px' }}
                              />
                              <TextField
                                label="Background"
                                value={widgetColors.customerInfoBgColor}
                                onChange={(value) => updateWidgetColor('customerInfoBgColor', value)}
                                autoComplete="off"
                              />
                            </InlineStack>
                            <InlineStack gap="200" align="start">
                              <input
                                type="color"
                                value={widgetColors.customerLabelColor}
                                onChange={(e) => updateWidgetColor('customerLabelColor', e.target.value)}
                                style={{ width: '50px', height: '35px', cursor: 'pointer', marginTop: '20px' }}
                              />
                              <TextField
                                label="Labels"
                                value={widgetColors.customerLabelColor}
                                onChange={(value) => updateWidgetColor('customerLabelColor', value)}
                                autoComplete="off"
                              />
                            </InlineStack>
                            <InlineStack gap="200" align="start">
                              <input
                                type="color"
                                value={widgetColors.customerValueColor}
                                onChange={(e) => updateWidgetColor('customerValueColor', e.target.value)}
                                style={{ width: '50px', height: '35px', cursor: 'pointer', marginTop: '20px' }}
                              />
                              <TextField
                                label="Values"
                                value={widgetColors.customerValueColor}
                                onChange={(value) => updateWidgetColor('customerValueColor', value)}
                                autoComplete="off"
                              />
                            </InlineStack>
                          </BlockStack>
                        </Grid.Cell>

                        <Grid.Cell columnSpan={{ xs: 6, sm: 4, md: 4, lg: 4, xl: 4 }}>
                          <BlockStack gap="300">
                            <Text variant="headingSm" as="h3">
                              Points Display
                            </Text>
                            <InlineStack gap="200" align="start">
                              <input
                                type="color"
                                value={widgetColors.pointsDisplayBgColor}
                                onChange={(e) => updateWidgetColor('pointsDisplayBgColor', e.target.value)}
                                style={{ width: '50px', height: '35px', cursor: 'pointer', marginTop: '20px' }}
                              />
                              <TextField
                                label="Background"
                                value={widgetColors.pointsDisplayBgColor}
                                onChange={(value) => updateWidgetColor('pointsDisplayBgColor', value)}
                                autoComplete="off"
                              />
                            </InlineStack>
                            <InlineStack gap="200" align="start">
                              <input
                                type="color"
                                value={widgetColors.pointsNumberColor}
                                onChange={(e) => updateWidgetColor('pointsNumberColor', e.target.value)}
                                style={{ width: '50px', height: '35px', cursor: 'pointer', marginTop: '20px' }}
                              />
                              <TextField
                                label="Points Number"
                                value={widgetColors.pointsNumberColor}
                                onChange={(value) => updateWidgetColor('pointsNumberColor', value)}
                                autoComplete="off"
                              />
                            </InlineStack>
                            <InlineStack gap="200" align="start">
                              <input
                                type="color"
                                value={widgetColors.pointsLabelColor}
                                onChange={(e) => updateWidgetColor('pointsLabelColor', e.target.value)}
                                style={{ width: '50px', height: '35px', cursor: 'pointer', marginTop: '20px' }}
                              />
                              <TextField
                                label="Points Label"
                                value={widgetColors.pointsLabelColor}
                                onChange={(value) => updateWidgetColor('pointsLabelColor', value)}
                                autoComplete="off"
                              />
                            </InlineStack>
                          </BlockStack>
                        </Grid.Cell>

                        <Grid.Cell columnSpan={{ xs: 6, sm: 4, md: 4, lg: 4, xl: 4 }}>
                          <BlockStack gap="300">
                            <Text variant="headingSm" as="h3">
                              Rewards Section
                            </Text>
                            <InlineStack gap="200" align="start">
                              <input
                                type="color"
                                value={widgetColors.discountsSectionBgColor}
                                onChange={(e) => updateWidgetColor('discountsSectionBgColor', e.target.value)}
                                style={{ width: '50px', height: '35px', cursor: 'pointer', marginTop: '20px' }}
                              />
                              <TextField
                                label="Section Background"
                                value={widgetColors.discountsSectionBgColor}
                                onChange={(value) => updateWidgetColor('discountsSectionBgColor', value)}
                                autoComplete="off"
                              />
                            </InlineStack>
                            <InlineStack gap="200" align="start">
                              <input
                                type="color"
                                value={widgetColors.discountsSectionTitleColor}
                                onChange={(e) => updateWidgetColor('discountsSectionTitleColor', e.target.value)}
                                style={{ width: '50px', height: '35px', cursor: 'pointer', marginTop: '20px' }}
                              />
                              <TextField
                                label="Section Title"
                                value={widgetColors.discountsSectionTitleColor}
                                onChange={(value) => updateWidgetColor('discountsSectionTitleColor', value)}
                                autoComplete="off"
                              />
                            </InlineStack>
                          </BlockStack>
                        </Grid.Cell>

                        <Grid.Cell columnSpan={{ xs: 6, sm: 4, md: 4, lg: 4, xl: 4 }}>
                          <BlockStack gap="300">
                            <Text variant="headingSm" as="h3">
                              Reward Items
                            </Text>
                            <InlineStack gap="200" align="start">
                              <input
                                type="color"
                                value={widgetColors.discountItemBgColor}
                                onChange={(e) => updateWidgetColor('discountItemBgColor', e.target.value)}
                                style={{ width: '50px', height: '35px', cursor: 'pointer', marginTop: '20px' }}
                              />
                              <TextField
                                label="Item Background"
                                value={widgetColors.discountItemBgColor}
                                onChange={(value) => updateWidgetColor('discountItemBgColor', value)}
                                autoComplete="off"
                              />
                            </InlineStack>
                            <InlineStack gap="200" align="start">
                              <input
                                type="color"
                                value={widgetColors.discountItemTitleColor}
                                onChange={(e) => updateWidgetColor('discountItemTitleColor', e.target.value)}
                                style={{ width: '50px', height: '35px', cursor: 'pointer', marginTop: '20px' }}
                              />
                              <TextField
                                label="Item Title"
                                value={widgetColors.discountItemTitleColor}
                                onChange={(value) => updateWidgetColor('discountItemTitleColor', value)}
                                autoComplete="off"
                              />
                            </InlineStack>
                            <InlineStack gap="200" align="start">
                              <input
                                type="color"
                                value={widgetColors.discountItemSubtitleColor}
                                onChange={(e) => updateWidgetColor('discountItemSubtitleColor', e.target.value)}
                                style={{ width: '50px', height: '35px', cursor: 'pointer', marginTop: '20px' }}
                              />
                              <TextField
                                label="Item Subtitle"
                                value={widgetColors.discountItemSubtitleColor}
                                onChange={(value) => updateWidgetColor('discountItemSubtitleColor', value)}
                                autoComplete="off"
                              />
                            </InlineStack>
                            <InlineStack gap="200" align="start">
                              <input
                                type="color"
                                value={widgetColors.discountButtonBgColor}
                                onChange={(e) => updateWidgetColor('discountButtonBgColor', e.target.value)}
                                style={{ width: '50px', height: '35px', cursor: 'pointer', marginTop: '20px' }}
                              />
                              <TextField
                                label="Button Background"
                                value={widgetColors.discountButtonBgColor}
                                onChange={(value) => updateWidgetColor('discountButtonBgColor', value)}
                                autoComplete="off"
                              />
                            </InlineStack>
                            <InlineStack gap="200" align="start">
                              <input
                                type="color"
                                value={widgetColors.discountButtonTextColor}
                                onChange={(e) => updateWidgetColor('discountButtonTextColor', e.target.value)}
                                style={{ width: '50px', height: '35px', cursor: 'pointer', marginTop: '20px' }}
                              />
                              <TextField
                                label="Button Text"
                                value={widgetColors.discountButtonTextColor}
                                onChange={(value) => updateWidgetColor('discountButtonTextColor', value)}
                                autoComplete="off"
                              />
                            </InlineStack>
                          </BlockStack>
                        </Grid.Cell>
                      </Grid>

                      <Divider />

                      <InlineStack gap="300">
                        <Button
                          variant="primary"
                          onClick={handleSaveWidgetColors}
                          loading={isSavingColors}
                        >
                          💾 Save Widget Colors
                        </Button>
                        <Button onClick={handleResetWidgetColors}>
                          🔄 Reset to Defaults
                        </Button>
                      </InlineStack>
                    </>
                  ) : (
                    <Banner tone="info">
                      <p>Loading widget colors...</p>
                    </Banner>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>

            {/* Widget Text Customization - Only show if API key is validated */}
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Text variant="headingMd" as="h2">
                    📝 Widget Text Customization
                  </Text>

                  {isLoadingColors ? (
                    <Box padding="400">
                      <Spinner accessibilityLabel="Loading widget text settings" size="small" />
                    </Box>
                  ) : widgetColors ? (
                    <>
                      <Grid>
                        <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                          <BlockStack gap="300">
                            <Text variant="headingSm" as="h3">
                              Guest Form
                            </Text>
                            <TextField
                              label="Header Title"
                              value={widgetColors.guestHeaderTitle || ''}
                              onChange={(value) => updateWidgetText('guestHeaderTitle', value || null)}
                              placeholder="Your rewards are waiting!"
                              helpText="Title shown to guest users"
                              autoComplete="off"
                            />
                            <TextField
                              label="Header Subtitle"
                              value={widgetColors.guestHeaderSubtitle || ''}
                              onChange={(value) => updateWidgetText('guestHeaderSubtitle', value || null)}
                              placeholder="Earn points, get exclusive discounts..."
                              helpText="Subtitle shown to guest users"
                              autoComplete="off"
                            />
                            <TextField
                              label="Benefits Title"
                              value={widgetColors.guestBenefitsTitle || ''}
                              onChange={(value) => updateWidgetText('guestBenefitsTitle', value || null)}
                              placeholder="Why Join {programName}?"
                              helpText="Benefits section title ({programName} will be replaced)"
                              autoComplete="off"
                            />
                            <TextField
                              label="Join Button Text"
                              value={widgetColors.guestJoinButtonText || ''}
                              onChange={(value) => updateWidgetText('guestJoinButtonText', value || null)}
                              placeholder="Join Now - It's Free!"
                              helpText="Text for the join button"
                              autoComplete="off"
                            />
                          </BlockStack>
                        </Grid.Cell>

                        <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                          <BlockStack gap="300">
                            <Text variant="headingSm" as="h3">
                              Logged-in User
                            </Text>
                            <TextField
                              label="Rewards Header Subtitle"
                              value={widgetColors.rewardsHeaderSubtitle || ''}
                              onChange={(value) => updateWidgetText('rewardsHeaderSubtitle', value || null)}
                              placeholder="Your rewards and points"
                              helpText="Subtitle in rewards header"
                              autoComplete="off"
                            />
                            <TextField
                              label="Points Label"
                              value={widgetColors.pointsLabelText || ''}
                              onChange={(value) => updateWidgetText('pointsLabelText', value || null)}
                              placeholder="Loyalty Points"
                              helpText="Label shown below points number"
                              autoComplete="off"
                            />
                            <TextField
                              label="Rewards Section Title"
                              value={widgetColors.rewardsSectionTitle || ''}
                              onChange={(value) => updateWidgetText('rewardsSectionTitle', value || null)}
                              placeholder="Available Rewards:"
                              helpText="Title for rewards section"
                              autoComplete="off"
                            />
                            <TextField
                              label="Apply Button Text"
                              value={widgetColors.applyButtonText || ''}
                              onChange={(value) => updateWidgetText('applyButtonText', value || null)}
                              placeholder="Apply"
                              helpText="Text for the apply button"
                              autoComplete="off"
                            />
                            <TextField
                              label="Applied Button Text"
                              value={widgetColors.appliedButtonText || ''}
                              onChange={(value) => updateWidgetText('appliedButtonText', value || null)}
                              placeholder="✅ Applied"
                              helpText="Text shown when reward is applied"
                              autoComplete="off"
                            />
                            <TextField
                              label="Need More Points Text"
                              value={widgetColors.needMorePointsText || ''}
                              onChange={(value) => updateWidgetText('needMorePointsText', value || null)}
                              placeholder="Need More Points"
                              helpText="Text when user doesn't have enough points"
                              autoComplete="off"
                            />
                            <TextField
                              label="Install Card Button Text"
                              value={widgetColors.installCardButtonText || ''}
                              onChange={(value) => updateWidgetText('installCardButtonText', value || null)}
                              placeholder="📱 Install Card"
                              helpText="Text for install card button"
                              autoComplete="off"
                            />
                            <TextField
                              label="No Rewards Text"
                              value={widgetColors.noRewardsText || ''}
                              onChange={(value) => updateWidgetText('noRewardsText', value || null)}
                              placeholder="No rewards available yet"
                              helpText="Message when no rewards available"
                              autoComplete="off"
                            />
                            <TextField
                              label="No Rewards Subtitle"
                              value={widgetColors.noRewardsSubtitle || ''}
                              onChange={(value) => updateWidgetText('noRewardsSubtitle', value || null)}
                              placeholder="Keep shopping to earn more points!"
                              helpText="Subtitle when no rewards available"
                              autoComplete="off"
                            />
                          </BlockStack>
                        </Grid.Cell>

                        <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                          <BlockStack gap="300">
                            <Text variant="headingSm" as="h3">
                              Success Screen
                            </Text>
                            <TextField
                              label="Success Title"
                              value={widgetColors.successTitle || ''}
                              onChange={(value) => updateWidgetText('successTitle', value || null)}
                              placeholder="Discount Applied Successfully!"
                              helpText="Title shown when discount is applied"
                              autoComplete="off"
                            />
                            <TextField
                              label="Go to Checkout Text"
                              value={widgetColors.goToCheckoutText || ''}
                              onChange={(value) => updateWidgetText('goToCheckoutText', value || null)}
                              placeholder="Go to Checkout Now"
                              helpText="Text for checkout button"
                              autoComplete="off"
                            />
                            <TextField
                              label="Continue Shopping Text"
                              value={widgetColors.continueShoppingText || ''}
                              onChange={(value) => updateWidgetText('continueShoppingText', value || null)}
                              placeholder="Continue Shopping"
                              helpText="Text for continue shopping button"
                              autoComplete="off"
                            />
                            <TextField
                              label="Remaining Balance Text"
                              value={widgetColors.remainingBalanceText || ''}
                              onChange={(value) => updateWidgetText('remainingBalanceText', value || null)}
                              placeholder="Remaining Balance: {points} points"
                              helpText="Text for remaining balance ({points} will be replaced)"
                              autoComplete="off"
                            />
                          </BlockStack>
                        </Grid.Cell>
                      </Grid>

                      <Divider />

                      <InlineStack gap="300">
                        <Button
                          variant="primary"
                          onClick={handleSaveWidgetColors}
                          loading={isSavingColors}
                        >
                          💾 Save Text Customization
                        </Button>
                        <Button onClick={() => {
                          if (!confirm('Are you sure you want to reset all text customization to defaults?')) {
                            return;
                          }
                          if (!widgetColors) return;
                          setWidgetColors({
                            ...widgetColors,
                            guestHeaderTitle: null,
                            guestHeaderSubtitle: null,
                            guestBenefitsTitle: null,
                            guestJoinButtonText: null,
                            rewardsHeaderSubtitle: null,
                            pointsLabelText: null,
                            rewardsSectionTitle: null,
                            applyButtonText: null,
                            appliedButtonText: null,
                            needMorePointsText: null,
                            installCardButtonText: null,
                            noRewardsText: null,
                            noRewardsSubtitle: null,
                            successTitle: null,
                            goToCheckoutText: null,
                            continueShoppingText: null,
                            remainingBalanceText: null,
                          });
                        }}>
                          🔄 Reset to Defaults
                        </Button>
                      </InlineStack>
                    </>
                  ) : (
                    <Banner tone="info">
                      <p>Loading widget text settings...</p>
                    </Banner>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>
          </>
        )}
      </Layout>
    </Page>
  );
};

export default Dashboard;
